/**
 * Wireless Advisor Algorithms
 * 
 * This file exports the different mesh placement algorithms and utility functions
 * for working with them.
 */

// Keep only the relevant algorithms
import rayTracingAlgorithm from './rayTracing';
import geneticAlgorithm from './geneticAlgorithm';
// import weightedRoomPlacement from './weightedRoom'; // Remove
import gradientDescentAlgorithm from './gradientDescent'; // Re-add gradientDescent
import coverageGapAlgorithm from './coverageGap';
// import simplePlacementAlgorithm from './simplePlacement'; // Remove

// Algorithm types (Re-add gradientDescent)
export const ALGORITHM_TYPES = {
  RAY_TRACING: 'ray_tracing',
  GENETIC: 'genetic',
  // WEIGHTED_ROOM: 'weighted_room', // Keep removed
  GRADIENT_DESCENT: 'gradient_descent', // Re-add
  COVERAGE_GAP: 'coverage_gap',
  // SIMPLE: 'simple', // Keep removed
};

/**
 * Get algorithm function by type (Re-add gradientDescent)
 * @param {string} type - Algorithm type
 * @returns {Function} Algorithm function or null if invalid
 */
export const getAlgorithm = (type) => {
  switch (type) {
    case ALGORITHM_TYPES.RAY_TRACING:
      return rayTracingAlgorithm;
    case ALGORITHM_TYPES.GENETIC:
      return geneticAlgorithm;
    // case ALGORITHM_TYPES.WEIGHTED_ROOM:
    //   return weightedRoomPlacement;
    case ALGORITHM_TYPES.GRADIENT_DESCENT: // Re-add
      return gradientDescentAlgorithm;
    case ALGORITHM_TYPES.COVERAGE_GAP:
      return coverageGapAlgorithm;
    // case ALGORITHM_TYPES.SIMPLE:
    //   return simplePlacementAlgorithm;
    default:
      console.warn(`Invalid algorithm type requested: ${type}. Defaulting to Coverage Gap.`);
      return coverageGapAlgorithm; // Default to a remaining one (Coverage Gap seems reasonable)
  }
};

/**
 * Get algorithm name by type
 * @param {string} type - Algorithm type
 * @returns {string} Algorithm name or 'Unknown Algorithm'
 */
export const getAlgorithmName = (type) => {
  switch (type) {
    case ALGORITHM_TYPES.RAY_TRACING:
      return 'Ray Tracing Simulation';
    case ALGORITHM_TYPES.GENETIC:
      return 'Genetic Algorithm';
    // case ALGORITHM_TYPES.WEIGHTED_ROOM:
    //   return 'Weighted Room Priority';
    case ALGORITHM_TYPES.GRADIENT_DESCENT: // Re-add
      return 'Gradient Descent';
    case ALGORITHM_TYPES.COVERAGE_GAP:
      return 'Coverage Gap Analysis';
    // case ALGORITHM_TYPES.SIMPLE:
    //   return 'Simple Placement';
    default:
      return 'Unknown Algorithm';
  }
};

/**
 * Get algorithm description by type
 * @param {string} type - Algorithm type
 * @returns {string} Algorithm description or empty string
 */
export const getAlgorithmDescription = (type) => {
  switch (type) {
    case ALGORITHM_TYPES.RAY_TRACING:
      return 'Simulates signal paths and interactions with materials for accurate coverage prediction.';
    case ALGORITHM_TYPES.GENETIC:
      return 'Uses evolutionary methods to find highly optimized mesh unit placements.';
    // case ALGORITHM_TYPES.WEIGHTED_ROOM:
    //   return 'Prioritizes coverage in specific rooms based on your activity preferences.';
    case ALGORITHM_TYPES.GRADIENT_DESCENT: // Re-add
      return 'Mathematically optimizes positions by iteratively improving coverage score.';
    case ALGORITHM_TYPES.COVERAGE_GAP:
      return 'Identifies areas with weak signal and strategically places units to fill them.';
    // case ALGORITHM_TYPES.SIMPLE:
    //   return 'Basic algorithm placing units evenly.';
    default:
      return '';
  }
};

/**
 * Get algorithm complexity by type
 * @param {string} type - Algorithm type
 * @returns {number} Algorithm complexity (1-5) or 0
 */
export const getAlgorithmComplexity = (type) => {
  switch (type) {
    case ALGORITHM_TYPES.RAY_TRACING:
      return 5; // Most complex
    case ALGORITHM_TYPES.GENETIC:
      return 4;
    // case ALGORITHM_TYPES.WEIGHTED_ROOM:
    //   return 3;
    case ALGORITHM_TYPES.GRADIENT_DESCENT: // Re-add
      return 4;
    case ALGORITHM_TYPES.COVERAGE_GAP:
      return 3;
    // case ALGORITHM_TYPES.SIMPLE:
    //   return 1;
    default:
      return 0;
  }
};

/**
 * Get algorithm accuracy by type
 * @param {string} type - Algorithm type
 * @returns {number} Algorithm accuracy (1-5) or 0
 */
export const getAlgorithmAccuracy = (type) => {
  switch (type) {
    case ALGORITHM_TYPES.RAY_TRACING:
      return 5; // Most accurate
    case ALGORITHM_TYPES.GENETIC:
      return 4;
    // case ALGORITHM_TYPES.WEIGHTED_ROOM:
    //   return 3;
    case ALGORITHM_TYPES.GRADIENT_DESCENT: // Re-add
      return 4;
    case ALGORITHM_TYPES.COVERAGE_GAP:
      return 3;
    // case ALGORITHM_TYPES.SIMPLE:
    //   return 2;
    default:
      return 0;
  }
};

export {
  // Export all included algorithms
  rayTracingAlgorithm,
  geneticAlgorithm,
  gradientDescentAlgorithm, // Re-add
  coverageGapAlgorithm,
};